// utils/buildCSV.js

function csvEscape(value) {
  const v = value == null ? "" : String(value);
  if (/[",\n;]/.test(v)) return '"' + v.replace(/"/g, '""') + '"';
  return v;
}

/**
 * Wandelt Eingabe-Daten (Eingabemaske oder UploadFile) in ein einheitliches CSV (long format) um
 *
 * Format:
 * UserId,Company,Category,Key,Value,Unit
 */
export function buildCSV({ userId, company, data }) {
  const lines = [];
  lines.push("UserId,Company,Category,Key,Value,Unit");

  // ---------- Allgemein ----------
  if (Array.isArray(data.bereiche)) {
    data.bereiche.forEach(b => {
      lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "bereich", csvEscape(b), ""].join(","));
    });
  }
  if (data.referenzjahr)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "jahr", csvEscape(data.referenzjahr), ""].join(","));
  if (data.umsatz)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "umsatz", csvEscape(data.umsatz), "CHF"].join(","));
  if (data.mitarbeitende)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "mitarbeitende", csvEscape(data.mitarbeitende), ""].join(","));
  if (data.produktiveMitarbeitende)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "produktive_mitarbeitende", csvEscape(data.produktiveMitarbeitende), ""].join(","));
  if (data.stunden)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "verkaufte_stunden", csvEscape(data.stunden), ""].join(","));
  if (data.reparaturen)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "reparaturen", csvEscape(data.reparaturen), ""].join(","));
  if (data.betriebsgrösse)
    lines.push([csvEscape(userId), csvEscape(company), "Allgemein", "betriebsgroesse", csvEscape(data.betriebsgrösse), "m2"].join(","));

  if (Array.isArray(data.lackierkabinen)) {
    data.lackierkabinen.forEach(r => {
      lines.push([csvEscape(userId), csvEscape(company), "Lackierkabine", "art", csvEscape(r.area ?? ""), ""].join(","));
    });
  }

  // ---------- Strom ----------
  if (data.strommix) {
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "strommix", csvEscape(data.strommix), ""].join(","));
  }
  if (data.renewableShare != null)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "renewableShare", csvEscape(data.renewableShare), "%"].join(","));
  if (data.mengeStrom != null)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "mengeStrom", csvEscape(data.mengeStrom), "kWh"].join(","));
  if (data.pvOwn)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "pvOwn", csvEscape(data.pvOwn), ""].join(","));
  if (data.pvMengeKwh != null)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "pvMengeKwh", csvEscape(data.pvMengeKwh), "kWh"].join(","));
  if (data.wärmepumpe)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "waermepumpe", csvEscape(data.wärmepumpe), ""].join(","));
  if (data.wärmepumpeBereiche)
    lines.push([csvEscape(userId), csvEscape(company), "Strom", "waermepumpe_bereiche", csvEscape(data.wärmepumpeBereiche), ""].join(","));

  // ---------- Heizung ----------
  if (Array.isArray(data.heizung)) {
    data.heizung.forEach(r => {
      lines.push([csvEscape(userId), csvEscape(company), csvEscape(r.area), csvEscape(r.carrier), csvEscape(r.amount), csvEscape(r.unit)].join(","));
    });
  }

  // ---------- Treibstoff ----------
  if (Array.isArray(data.treibstoff)) {
    data.treibstoff.forEach(r => {
      lines.push([csvEscape(userId), csvEscape(company), "Treibstoff/Elektrische Fahrzeuge", csvEscape(r.type), csvEscape(r.amount), csvEscape(r.unit)].join(","));
    });
  }

  // ---------- Fossile Fahrzeuge ----------
  if (Array.isArray(data.fossileFahrzeuge)) {
    data.fossileFahrzeuge.forEach(r => {
      lines.push([csvEscape(userId), csvEscape(company), "Fossile Fahrzeuge", csvEscape(r.type), csvEscape(r.amount), ""].join(","));
    });
  }

  // ---------- Beleuchtung ----------
  if (Array.isArray(data.beleuchtung)) {
    data.beleuchtung.forEach(r => {
      lines.push([csvEscape(userId), csvEscape(company), "Beleuchtung", csvEscape(r.type), csvEscape(r.amount), ""].join(","));
    });
  }

  // ---------- Lack ----------
  if (data.lackmix) {
    lines.push([csvEscape(userId), csvEscape(company), "Lack", "lackmix", csvEscape(data.lackmix), ""].join(","));
  }
  if (data.lufttrocknendShare != null)
    lines.push([csvEscape(userId), csvEscape(company), "Lack", "lufttrocknendShare", csvEscape(data.lufttrocknendShare), "%"].join(","));

  // ---------- Emissionen ----------
  if (data.gesamt_co2 != null) {
    lines.push([csvEscape(userId), csvEscape(company), "Emissionen", "gesamt_co2", csvEscape(data.gesamt_co2), "t"].join(","));
  }
  if (data.spezifisch_co2 != null)
    lines.push([csvEscape(userId), csvEscape(company), "Emissionen", "spezifisch_co2", csvEscape(data.spezifisch_co2), "kg/m²"].join(","));
  if (data.emissionen?.strom?.wert_tonnen != null)
    lines.push([csvEscape(userId), csvEscape(company), "Emissionen", "strom", csvEscape(data.emissionen.strom.wert_tonnen), "t"].join(","));
  if (data.emissionen?.heizung?.wert_tonnen != null)
    lines.push([csvEscape(userId), csvEscape(company), "Emissionen", "heizung", csvEscape(data.emissionen.heizung.wert_tonnen), "t"].join(","));
  if (data.emissionen?.fuhrpark?.wert_tonnen != null)
    lines.push([csvEscape(userId), csvEscape(company), "Emissionen", "fuhrpark", csvEscape(data.emissionen.fuhrpark.wert_tonnen), "t"].join(","));

  // ---------- Verbrauchsmaterialien & Abfall ----------
  if (data.verbrauchsmaterialLack != null) {
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "lack", csvEscape(data.verbrauchsmaterialLack), "CHF"].join(","));
  }
  if (data.verbrauchsmaterialAllg != null)
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "verbrauchsmaterialAllg", csvEscape(data.verbrauchsmaterialAllg), "CHF"].join(","));
  if (data.verbrauchsmaterialErsatzteile != null)
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "ersatzteile", csvEscape(data.verbrauchsmaterialErsatzteile), "CHF"].join(","));
  if (data.abfallTonnen != null)
    lines.push([csvEscape(userId), csvEscape(company), "Abfall", "mengeTonnen", csvEscape(data.abfallTonnen), "t"].join(","));

  if (data.verbrauchsmaterialien?.lack_chf != null) {
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "lack", csvEscape(data.verbrauchsmaterialien.lack_chf), "CHF"].join(","));
  }
  if (data.verbrauchsmaterialien?.materialien_chf != null)
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "verbrauchsmaterialAllg", csvEscape(data.verbrauchsmaterialien.materialien_chf), "CHF"].join(","));
  if (data.verbrauchsmaterialien?.ersatzteile_chf != null)
    lines.push([csvEscape(userId), csvEscape(company), "Einkauf", "ersatzteile", csvEscape(data.verbrauchsmaterialien.ersatzteile_chf), "CHF"].join(","));
  if (data.abfall?.menge_tonnen != null)
    lines.push([csvEscape(userId), csvEscape(company), "Abfall", "mengeTonnen", csvEscape(data.abfall.menge_tonnen), "t"].join(","));

  return lines.join("\n");
}